import type * as React from "react";
import { Input as InputPrimitive } from "@base-ui/react/input";
import { cva, type VariantProps } from "cva";
import { cn } from "#ui-atoms/lib/utils";
import type { OptionalTenantProp } from "../../../types";

const innerInputVariants = cva({
  base: "flex h-10 w-full min-w-0 bg-transparent px-[calc(--spacing(3)-1px)] py-[calc(--spacing(1.5)-1px)] text-base outline-none placeholder:text-muted-foreground/64 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
  variants: {
    variant: {
      default: undefined,
      ghost: "hover:bg-accent hover:text-accent-foreground",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type BaseInputVariant = VariantProps<typeof innerInputVariants>["variant"];

type BaseInputProps = Omit<
  InputPrimitive.Props & React.RefAttributes<HTMLInputElement>,
  "size"
> &
  OptionalTenantProp & {
    size?: "sm" | "default" | "lg" | number;
    unstyled?: boolean;
    variant?: BaseInputVariant;
    /**
     * Classes for the visible `<input>` element. Use this to style the actual
     * input (e.g. inside InputGroup, where the wrapper has `display: contents`
     * and any wrapper classes have no visual effect).
     */
    className?: string;
    /**
     * Classes for the styled wrapper `<span>`. Use this for chrome that
     * targets the input's container (e.g. `has-*` selectors that key off the
     * input's state, max-width on standalone Input).
     */
    containerClassName?: string;
  };

function BaseInput({
  className,
  containerClassName,
  size = "default",
  unstyled = false,
  variant = "default",
  type = "text",
  ...props
}: BaseInputProps) {
  return (
    <span
      className={
        cn(
          !unstyled &&
            "relative inline-flex w-full rounded-lg border border-input bg-background bg-clip-padding text-base/5 shadow-xs ring-ring/24 transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 has-aria-invalid:border-destructive/36 has-focus-visible:border-ring has-disabled:opacity-64 has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none has-focus-visible:ring-[3px] sm:text-sm dark:bg-input/32 dark:not-in-data-[slot=group]:bg-clip-border dark:has-aria-invalid:ring-destructive/24 dark:not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/8%)]",
          containerClassName,
        ) || undefined
      }
      data-size={size}
      data-slot="input-control"
    >
      <InputPrimitive
        className={cn(
          innerInputVariants({ variant }),
          "rounded-[inherit]",
          size === "sm" &&
            "px-[calc(--spacing(2.5)-1px)] py-[calc(--spacing(1)-1px)]",
          size === "lg" && "py-[calc(--spacing(2)-1px)]",
          type === "search" &&
            "[&::-webkit-search-cancel-button]:cursor-pointer",
          type === "file" &&
            "text-muted-foreground file:me-3 file:bg-transparent file:font-medium file:text-foreground file:text-sm",
          className,
        )}
        data-slot="input"
        size={typeof size === "number" ? size : undefined}
        type={type}
        {...props}
      />
    </span>
  );
}

export { BaseInput, type BaseInputProps };
